import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppUserSettings, AppUserViewModel, PauseMode, RightEnum, TimeEntryModeEnum } from '@/services/api/nswag/stapiclient_generated';
import { stAppUserApi } from '@/services/st-api';
import { DateTime } from 'luxon';
import { clone } from '@/utlitities/helper';
import numeral from 'numeral';
import { isDarkColor, setPrimaryColor, setSecondaryColor } from '@/utlitities/color';
import { i18n } from '@/localization';
import { Settings } from 'luxon';

@Module({ dynamic: true, store, name: 'UserModule', namespaced: true })
export default class UserModule extends VuexModule {
  public userSettingsInternal: AppUserSettings = new AppUserSettings();
  public dataLoaded: boolean = false;
  public isLoggedIn: boolean = false;
  public currentUser: AppUserViewModel = new AppUserViewModel();

  public templates: any[] = [
    {
      id: 'SpikeTime Clean',
      filename: '',
      isDark: false,
    },
    {
      id: 'Binding Dark',
      filename: "url('/bg/binding_dark.png')",
      isDark: false,
      textOnBackground: '#eee',
    },
    {
      id: 'Brickwall',
      filename: "url('/bg/brickwall.png')",
      isDark: false,
    },
    {
      id: 'Congruent Outline',
      filename: "url('/bg/congruent_outline.png')",
      isDark: false,
      textOnBackground: '#fff',
    },
    {
      id: 'Fresh Snow',
      filename: "url('/bg/fresh_snow.png')",
      isDark: false,
    },
    {
      id: 'Geometry',
      filename: "url('/bg/geometry.png')",
      isDark: false,
    },
    {
      id: 'Giftly',
      filename: "url('/bg/giftly.png')",
      isDark: false,
    },
    {
      id: 'Green Cup',
      filename: "url('/bg/green_cup.png')",
      isDark: false,
    },
    {
      id: 'Grey washed wall',
      filename: "url('/bg/grey_wash_wall.png')",
      isDark: false,
      textOnBackground: '#eee',
    },
    {
      id: 'Hoffmann',
      filename: "url('/bg/hoffmann.png')",
      isDark: false,
    },
    {
      id: 'Photography',
      filename: "url('/bg/photography.png')",
      isDark: false,
    },
    {
      id: 'Purty Wood',
      filename: "url('/bg/purty_wood.png')",
      isDark: false,
    },
    {
      id: 'Other Wood',
      filename: "url('/bg/retina_wood.png')",
      isDark: false,
    },
    {
      id: 'Ricepaper',
      filename: "url('/bg/ricepaper_v3.png')",
      isDark: false,
    },
    {
      id: 'Sativa',
      filename: "url('/bg/sativa.png')",
      isDark: false,
    },
    {
      id: 'Tileable Wood',
      filename: "url('/bg/tileable_wood_texture.png')",
      isDark: false,
    },
    {
      id: 'Tree bark',
      filename: "url('/bg/tree_bark.png')",
      isDark: false,
    },
    {
      id: 'Whitewall',
      filename: "url('/bg/whitewall_3.png')",
      isDark: false,
    },
  ];

  get primaryColorIsDark() {
    return isDarkColor(this.userSettings.mainColor ?? '#357ebd');
  }

  get secondaryColorIsDark() {
    return isDarkColor(this.userSettings.secondaryColor ?? '#6ec06e');
  }

  get useManualPause() {
    return this.userSettings.pauseMode == PauseMode.Manual;
  }

  get useAutoPause() {
    return this.userSettings.pauseMode == PauseMode.Auto;
  }

  get usePauseAtTimeEntry() {
    return this.userSettings.pauseMode == PauseMode.TimeEntry && this.userSettings.timeEntryMode != TimeEntryModeEnum.Duration;
  }

  get userSettings() {
    if (!this.userSettingsInternal?.appUserId) {
      return {};
    }

    let result = clone(this.userSettingsInternal);

    for (let rightEnumKey in RightEnum) {
      result[rightEnumKey.toString()] = this.userSettingsInternal.userRights?.some((x) => x.toString() == rightEnumKey);
    }

    return result;
  }

  get userNowDateTime() {
    if (!this.userSettings?.userNowDateTime) {
      return DateTime.local();
    }
    return DateTime.fromISO(this.userSettings.userNowDateTime);
  }

  get userNowDateString() {
    return this.userNowDateTime.toFormat('yyyy-MM-dd');
  }

  @Mutation
  setUserSettings(data: AppUserSettings) {
    this.userSettingsInternal = data;
    this.isLoggedIn = true;
    this.dataLoaded = true;
  }

  @Mutation
  setCurrentUser(data: AppUserViewModel) {
    this.currentUser = data;
  }

  @Mutation
  resetStore() {
    this.userSettingsInternal = new AppUserSettings();
    this.isLoggedIn = false;
    this.dataLoaded = false;
  }

  @Action
  async reloadStore() {
    await this.ensureUserData();
  }

  @Action
  async ensureUserData() {
    numeral.locale('de'); //TODO: usersettings!
    this.setCurrentUser(await stAppUserApi.getCurrentUser());
    this.setUserSettings(await stAppUserApi.getCurrentUserSettings());

    i18n.locale = this.userSettingsInternal.language!;
    numeral.locale(this.userSettingsInternal.language);
    Settings.defaultLocale = this.userSettingsInternal.language!;

    setPrimaryColor(this.userSettings.mainColor ?? '#357ebd');
    setSecondaryColor(this.userSettings.secondaryColor ?? '#6ec06e');

    const template = this.templates.find((_) => _.id == this.userSettings.templateName);

    if (template) {
      document.documentElement.style.setProperty('--background-image', template.filename);

      if (template.textOnBackground) {
        document.documentElement.style.setProperty('--text-on-background', template.textOnBackground);
        document.documentElement.style.setProperty('--text-on-background-lighter', template.textOnBackgroundLighter);
      } else {
        document.documentElement.style.setProperty('--text-on-background', 'initial');
        document.documentElement.style.setProperty('--text-on-background-lighter', 'initial');
      }
    } else {
      document.documentElement.style.setProperty('--background-image', '');
    }
  }
}

export const UserStore = getModule(UserModule);
